import {
  ENTITY_NAME,
  EVENT_TYPE,
  MORTGAGE,
  HOME_AFFORDABILITY,
} from '../../lib/analytics-constants';
import type { Calculator } from '../../lib/analytics-constants';

import type { FieldName } from './types';

export const PRINCIPAL_AND_INTEREST = 'principalAndInterest';
export const PROPERTY_TAXES = 'propertyTaxes';
export const HOMEOWNERS_INSURANCE = 'homeownersInsurance';
export const HOMEOWNERS_ASSOCIATION_FEES = 'homeownersAssociationFees';
export const PRIVATE_MORTGAGE_INSURANCE = 'privateMortgageInsurance';
export const MORTGAGE_PAYMENT = 'mortgagePayment';
export const MORTGAGE_INSURANCE = 'mortgageInsurance';
export const DOWN_PAYMENT = 'downPayment';
export const TOTAL_CLOSING_COST = 'totalClosingCost';

export const MORTGAGE_FIELDS: FieldName[] = [
  PRINCIPAL_AND_INTEREST,
  PROPERTY_TAXES,
  HOMEOWNERS_INSURANCE,
  HOMEOWNERS_ASSOCIATION_FEES,
  PRIVATE_MORTGAGE_INSURANCE,
];

export const MORTGAGE_FIELDS_CONTROL_PANEL: FieldName[] = [
  PRINCIPAL_AND_INTEREST,
  PRIVATE_MORTGAGE_INSURANCE,
];

export const HOME_AFFORDABILITY_FIELDS: FieldName[] = [
  MORTGAGE_PAYMENT,
  PROPERTY_TAXES,
  MORTGAGE_INSURANCE,
  HOMEOWNERS_INSURANCE,
  HOMEOWNERS_ASSOCIATION_FEES,
  PRIVATE_MORTGAGE_INSURANCE,
];

export const HOME_AFFORDABILITY_FIELDS_DOWN_PAYMENTS: FieldName[] = [
  DOWN_PAYMENT,
  TOTAL_CLOSING_COST,
];

// Calculators
export const HOME_AFFORDABILITY_DOWN_PAYMENTS = 'homeAffordabilityDownPayments';
export const MORTGAGE_CONTROL_PANEL = 'mortgageControlPanel';

const OTHER_VIEWS = [
  MORTGAGE_CONTROL_PANEL,
  HOME_AFFORDABILITY_DOWN_PAYMENTS,
] as const;
type CalculatorsOtherViews = typeof OTHER_VIEWS[number];

type CalculatorsWithControlPanelViews =
  | Extract<Calculator, 'mortgage' | 'homeAffordability'> // at the moment we only have mortgage & home affordability
  | CalculatorsOtherViews;

type CalculatorFields = Record<CalculatorsWithControlPanelViews, FieldName[]>;
export const CALCULATOR_FIELDS: CalculatorFields = {
  [MORTGAGE]: MORTGAGE_FIELDS,
  [MORTGAGE_CONTROL_PANEL]: MORTGAGE_FIELDS_CONTROL_PANEL,
  [HOME_AFFORDABILITY]: HOME_AFFORDABILITY_FIELDS,
  [HOME_AFFORDABILITY_DOWN_PAYMENTS]: HOME_AFFORDABILITY_FIELDS_DOWN_PAYMENTS,
};

const HOMEOWNERS_INSURANCE_ANALYTICS = {
  enabled: true,
  payload: {
    legacy: {
      formName: 'config-builder', // taken from PROD
    },
    bedrock: {
      eventType: EVENT_TYPE.HOMEOWNER_INSURANCE_UPDATED,
      was_prefilled: true,
      form_name: ENTITY_NAME.MRT_CALCULATOR_MONTHLY_PAYMENT,
    },
  },
};

const HAC_HOMEOWNERS_INSURANCE_ANALYTICS = {
  enabled: true,
  payload: {
    legacy: {
      formName: 'config-builder', // taken from PROD
    },
  },
};

const HOMEOWNER_ASSOCIATION_FEES_ANALYTICS = {
  enabled: true,
  payload: {
    legacy: {
      formName: 'config-builder', // taken from PROD
    },
    bedrock: {
      eventType: EVENT_TYPE.HOME_ASSOCIATION_FEE_UPDATED,
      was_prefilled: true,
      form_name: ENTITY_NAME.MRT_CALCULATOR_MONTHLY_PAYMENT,
    },
  },
};

const HAC_HOMEOWNER_ASSOCIATION_FEES_ANALYTICS = {
  enabled: true,
  payload: {
    legacy: {
      formName: 'config-builder', // taken from PROD
    },
  },
};

const PROPERTY_TAXES_INPUT_ANALYTICS = {
  enabled: true,
  payload: {
    legacy: {
      formName: 'config-builder', // taken from PROD
    },
    bedrock: {
      eventType: EVENT_TYPE.MORTGAGE_PROPERTY_TAX_UPDATED,
      was_prefilled: true,
      form_name: ENTITY_NAME.MRT_CALCULATOR_MONTHLY_PAYMENT,
    },
  },
};

const HAC_PROPERTY_TAXES_INPUT_ANALYTICS = {
  enabled: true,
  payload: {
    legacy: {
      formName: 'config-builder', // taken from PROD
    },
  },
};

const PRIVATE_MORTGAGE_INSURANCE_ANALYTICS = {
  enabled: true,
  payload: {
    legacy: {
      formName: 'config-builder', // taken from PROD
    },
  },
};

const MORTGAGE_INSURANCE_ANALYTICS = {
  enabled: true,
  payload: {
    legacy: {
      formName: 'config-builder', // taken from PROD
    },
  },
};

const MORTGAGE_INPUTS_ANALYTICS_HASH = {
  [PROPERTY_TAXES]: PROPERTY_TAXES_INPUT_ANALYTICS,
  [HOMEOWNERS_INSURANCE]: HOMEOWNERS_INSURANCE_ANALYTICS,
  [HOMEOWNERS_ASSOCIATION_FEES]: HOMEOWNER_ASSOCIATION_FEES_ANALYTICS,
  [PRIVATE_MORTGAGE_INSURANCE]: PRIVATE_MORTGAGE_INSURANCE_ANALYTICS,
};

const HOME_AFFORDABILITY_ANALYTICS_HASH = {
  [PROPERTY_TAXES]: HAC_PROPERTY_TAXES_INPUT_ANALYTICS,
  [HOMEOWNERS_INSURANCE]: HAC_HOMEOWNERS_INSURANCE_ANALYTICS,
  [HOMEOWNERS_ASSOCIATION_FEES]: HAC_HOMEOWNER_ASSOCIATION_FEES_ANALYTICS,
  [PRIVATE_MORTGAGE_INSURANCE]: PRIVATE_MORTGAGE_INSURANCE_ANALYTICS,
  [MORTGAGE_INSURANCE]: MORTGAGE_INSURANCE_ANALYTICS,
};

export const CALCULATORS_INPUTS_HASH = {
  [MORTGAGE]: MORTGAGE_INPUTS_ANALYTICS_HASH,
  [HOME_AFFORDABILITY]: HOME_AFFORDABILITY_ANALYTICS_HASH,
};
